import { useTranslation } from "react-i18next";
import { RadioButton, Select, Text, TextField } from "@foris/avocado-suite";
import type { Block } from "@models/ISchema";
import type { SessionAssignationRowPatch } from "@services/types/session-assignation";
import type {
  SessionAssignationActionResult,
  SessionAssignationEditorTarget,
} from "../../types/session-assignation";
import { useSessionTimeDraft } from "../../hooks/session-assignation/useSessionTimeDraft";
import AssignmentEditorDrawer from "../assignment-editor-drawer";
import styles from "./sessionAssignationEditor.module.scss";

interface SessionAssignationEditorProps {
  target: SessionAssignationEditorTarget | null;
  isOpen: boolean;
  blocks: readonly Block[];
  canChangeTime: boolean;
  onDirty: (dirty: boolean) => void;
  onApply: (patch: SessionAssignationRowPatch) => SessionAssignationActionResult;
  onRequestClose: () => void;
  onClose: () => void;
}

const SessionAssignationEditor = ({
  target,
  isOpen,
  blocks,
  canChangeTime,
  onDirty,
  onApply,
  onRequestClose,
  onClose,
}: SessionAssignationEditorProps) => {
  const { t } = useTranslation();
  const { values, change, setMode, startOptions, endOptions, issue, setIssue, patch } =
    useSessionTimeDraft(target, blocks, onDirty, isOpen);
  return (
    <AssignmentEditorDrawer
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={onRequestClose}
      title={t("assignation-edit.session-view.editor.time-title")}
      disabled={!canChangeTime}
      onApply={() => {
        const next = patch();
        if (!next) return;
        const result = onApply(next);
        if (result.ok === false) setIssue(result.code);
      }}
    >
      <div className={styles.fields}>
        <fieldset className={styles.mode} disabled={!canChangeTime}>
          <legend className={styles.srOnly}>
            {t("assignation-edit.session-view.editor.schedule-mode")}
          </legend>
          <RadioButton
            name="session-schedule-mode"
            labelRight={t("assignation-edit.session-view.editor.scheduled")}
            checked={values.scheduleMode === "scheduled"}
            onChange={() => setMode("scheduled")}
          />
          <RadioButton
            name="session-schedule-mode"
            labelRight={t("assignation-edit.session-view.editor.unscheduled")}
            checked={values.scheduleMode === "unscheduled"}
            onChange={() => setMode("unscheduled")}
          />
        </fieldset>
        {values.scheduleMode === "scheduled" && (
          <div className={styles.timeRange}>
            <Select
              label={t("assignation-edit.session-view.editor.start")}
              disabled={!canChangeTime}
              options={startOptions}
              value={startOptions.find(option => option.value === values.startingTime) ?? null}
              onChange={(option: { value: string }) =>
                change({ startingTime: option?.value ?? null })
              }
            />
            <span aria-hidden>–</span>
            <Select
              label={t("assignation-edit.session-view.editor.end")}
              disabled={!canChangeTime}
              options={endOptions}
              value={endOptions.find(option => option.value === values.endingTime) ?? null}
              onChange={(option: { value: string }) =>
                change({ endingTime: option?.value ?? null })
              }
            />
          </div>
        )}
        {values.scheduleMode === "unscheduled" && (
          <TextField
            className={styles.blocks}
            label={t("assignation-edit.session-view.editor.blocks")}
            type="text"
            inputMode="numeric"
            disabled={!canChangeTime}
            value={values.blocksCountInput}
            onChange={event => change({ blocksCountInput: event.target.value })}
          />
        )}
        {issue && (
          <Text role="alert" className={styles.error}>
            {t(`assignation-edit.session-view.errors.${issue}`, {
              defaultValue: t("assignation-edit.session-view.errors.ACTION_FAILED"),
            })}
          </Text>
        )}
      </div>
    </AssignmentEditorDrawer>
  );
};

export default SessionAssignationEditor;
